import type { StatBlock, StatId } from '../domain/stats.ts';

/**
 * RULE DCC-STAT-MOD-001 - Stat Mod table.
 * Sources: Rules for Survival p.11 (Table 1); Crawlers & Customization p.14 (Table 9).
 * Both books print an identical table.
 *
 * Ranges are inclusive. The final band (300+) has no upper bound.
 */
export interface StatModBand {
  min: number;
  max: number | null;
  mod: number;
}

export const STAT_MOD_TABLE: readonly StatModBand[] = [
  { min: 1, max: 2, mod: 1 },
  { min: 3, max: 5, mod: 2 },
  { min: 6, max: 9, mod: 3 },
  { min: 10, max: 19, mod: 4 },
  { min: 20, max: 49, mod: 5 },
  { min: 50, max: 99, mod: 6 },
  { min: 100, max: 149, mod: 7 },
  { min: 150, max: 199, mod: 8 },
  { min: 200, max: 299, mod: 9 },
  { min: 300, max: null, mod: 10 },
];

/**
 * Returns the Stat Mod for a Stat score.
 *
 * RULE DCC-STAT-MOD-002 - the Mod is always taken from the ENHANCED Stat value.
 * Source: Crawlers & Customization p.14. Callers must pass an Enhanced score.
 *
 * The supplied sources do not define a Stat Mod for a score below 1, so this
 * throws rather than inventing a value.
 */
export function statModForScore(score: number): number {
  if (!Number.isInteger(score)) {
    throw new Error(`Stat score must be an integer, received ${score}`);
  }
  if (score < 1) {
    throw new Error(
      `No Stat Mod is defined in the supplied sources for a Stat score below 1 (received ${score})`,
    );
  }
  for (const band of STAT_MOD_TABLE) {
    if (score >= band.min && (band.max === null || score <= band.max)) {
      return band.mod;
    }
  }
  throw new Error(`No Stat Mod band matched score ${score}`);
}

export function statModsForBlock(stats: StatBlock): Record<StatId, number> {
  return {
    str: statModForScore(stats.str),
    int: statModForScore(stats.int),
    con: statModForScore(stats.con),
    dex: statModForScore(stats.dex),
    cha: statModForScore(stats.cha),
  };
}
